import { useCallback, useState } from 'react';
import {
  PublicKey,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js';
import {
  decodeOreMinerSnapshot,
  getManagedMinerAuthPda,
  getOreMinerPda,
  mmCheckpointInstruction,
  mmClaimOreInstruction,
  mmClaimSolInstruction,
} from '../lib/evore';
import { getConnection } from '../lib/rpc';
import type { UnifiedWallet } from '../auth/useWallet';

export type WithdrawStep =
  | 'idle'
  | 'preparing'
  | 'signing'
  | 'submitting'
  | 'confirming'
  | 'success'
  | 'error';

export interface UseWithdrawResult {
  step: WithdrawStep;
  error: string | null;
  signature: string | null;
  withdrawAll: (manager: PublicKey) => Promise<void>;
  claimOre: (manager: PublicKey) => Promise<void>;
  reset: () => void;
}

/**
 * Sunset withdraw flow. The on-chain `mm_claim_sol` processor transfers the MMA's
 * full lamport balance (including the rent reserve) to the signer, closing the
 * MMA. So a single `mmClaimSol`, optionally preceded by a checkpoint to roll a
 * just-finished round's winnings in, withdraws everything.
 *
 * Mirrors `POST /api/accounts/:accountId/claim-sol-full` from the original server
 * (server/src/routes/accounts.routes.ts:1364).
 */
export function useWithdraw(wallet: UnifiedWallet): UseWithdrawResult {
  const [step, setStep] = useState<WithdrawStep>('idle');
  const [error, setError] = useState<string | null>(null);
  const [signature, setSignature] = useState<string | null>(null);

  const reset = useCallback(() => {
    setStep('idle');
    setError(null);
    setSignature(null);
  }, []);

  const sendInstructions = useCallback(
    async (
      instructions: ReturnType<typeof mmClaimSolInstruction>[],
      signer: PublicKey
    ) => {
      const sign = wallet.signTransaction;
      if (!sign) throw new Error('sunset.errors.walletNotConnected');
      const connection = getConnection();

      const { blockhash, lastValidBlockHeight } =
        await connection.getLatestBlockhash('confirmed');

      const message = new TransactionMessage({
        payerKey: signer,
        recentBlockhash: blockhash,
        instructions,
      }).compileToV0Message();

      const tx = new VersionedTransaction(message);

      setStep('signing');
      const signed = await sign(tx);

      setStep('submitting');
      const sig = await connection.sendTransaction(signed, {
        skipPreflight: false,
        maxRetries: 3,
      });
      setSignature(sig);

      setStep('confirming');
      const confirmation = await connection.confirmTransaction(
        { signature: sig, blockhash, lastValidBlockHeight },
        'confirmed'
      );
      if (confirmation.value.err) {
        throw new Error(
          `Transaction failed: ${JSON.stringify(confirmation.value.err)}`
        );
      }
    },
    [wallet]
  );

  const withdrawAll = useCallback(
    async (manager: PublicKey) => {
      const signer = wallet.publicKey;
      if (!signer) {
        setError('sunset.errors.walletNotConnected');
        setStep('error');
        return;
      }

      try {
        setError(null);
        setSignature(null);
        setStep('preparing');

        const connection = getConnection();
        const [mma] = getManagedMinerAuthPda(manager, 0n);
        const [oreMiner] = getOreMinerPda(mma);

        const [mmaInfo, minerInfo] = await connection.getMultipleAccountsInfo([
          mma,
          oreMiner,
        ]);

        if (!mmaInfo || mmaInfo.lamports === 0) {
          throw new Error('sunset.errors.noSolBalance');
        }

        const instructions = [];

        if (minerInfo) {
          const snapshot = decodeOreMinerSnapshot(minerInfo.data);
          if (snapshot.roundId > snapshot.checkpointId) {
            instructions.push(
              mmCheckpointInstruction(signer, manager, snapshot.roundId, 0n)
            );
          }
        }

        instructions.push(mmClaimSolInstruction(signer, manager, 0n));

        await sendInstructions(instructions, signer);
        setStep('success');
      } catch (err) {
        console.error('[useWithdraw] withdrawAll failed:', err);
        // Error string may be either a translation key (sunset.errors.*) or a
        // raw RPC error message — t() in the component falls through to the
        // raw string when the key isn't found, so both render cleanly.
        setError(err instanceof Error ? err.message : 'sunset.errors.withdrawFailed');
        setStep('error');
      }
    },
    [wallet, sendInstructions]
  );

  const claimOre = useCallback(
    async (manager: PublicKey) => {
      const signer = wallet.publicKey;
      if (!signer) {
        setError('sunset.errors.walletNotConnected');
        setStep('error');
        return;
      }

      try {
        setError(null);
        setSignature(null);
        setStep('preparing');

        await sendInstructions(
          [mmClaimOreInstruction(signer, manager, 0n)],
          signer
        );
        setStep('success');
      } catch (err) {
        console.error('[useWithdraw] claimOre failed:', err);
        setError(err instanceof Error ? err.message : 'sunset.errors.claimOreFailed');
        setStep('error');
      }
    },
    [wallet, sendInstructions]
  );

  return { step, error, signature, withdrawAll, claimOre, reset };
}
